import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { evaluationKeys } from './useEvaluations';
import type { QuestionBankQuestion, QuestionType } from '@lms/shared/types';

export const questionBankKeys = {
  all: ['question-bank'] as const,
  lists: () => [...questionBankKeys.all, 'list'] as const,
  list: (params: QuestionBankFilters) => [...questionBankKeys.lists(), params] as const,
  details: () => [...questionBankKeys.all, 'detail'] as const,
  detail: (id: string) => [...questionBankKeys.details(), id] as const,
};

export interface QuestionBankFilters {
  programId?: string;
  subjectId?: string;
  type?: QuestionType;
  search?: string;
  page?: number;
  limit?: number;
}

export interface QuestionBankOptionInput {
  text: string;
  isCorrect: boolean;
  order: number;
}

export interface QuestionBankQuestionInput {
  programId: string;
  subjectId?: string;
  question: string;
  type: QuestionType;
  explanation?: string;
  points: number;
  options: QuestionBankOptionInput[];
}

export function useQuestionBank(filters: QuestionBankFilters) {
  return useQuery({
    queryKey: questionBankKeys.list(filters),
    queryFn: () => apiClient.questionBank.list(filters),
  });
}

export function useQuestionBankQuestion(id: string) {
  return useQuery({
    queryKey: questionBankKeys.detail(id),
    queryFn: () => apiClient.questionBank.get(id),
    enabled: Boolean(id),
  });
}

function invalidateQuestionBank(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: questionBankKeys.all });
}

export function useCreateQuestionBankQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: QuestionBankQuestionInput) => apiClient.questionBank.create(data),
    onSuccess: () => void invalidateQuestionBank(queryClient),
  });
}

export function useUpdateQuestionBankQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuestionBankQuestionInput> }) =>
      apiClient.questionBank.update(id, data),
    onSuccess: (_, variables) => {
      void invalidateQuestionBank(queryClient);
      void queryClient.invalidateQueries({ queryKey: questionBankKeys.detail(variables.id) });
    },
  });
}

export function useDeleteQuestionBankQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.questionBank.delete(id),
    onSuccess: () => void invalidateQuestionBank(queryClient),
  });
}

export function useBulkCreateQuestionBankQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programId,
      subjectId,
      questions,
    }: {
      programId: string;
      subjectId?: string;
      questions: Omit<QuestionBankQuestionInput, 'programId' | 'subjectId'>[];
    }) => apiClient.questionBank.bulkCreate({ programId, subjectId, questions }),
    onSuccess: () => void invalidateQuestionBank(queryClient),
  });
}

export function useCopyQuestionBankQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { targetProgramId: string; targetSubjectId?: string; questionIds: string[] }) =>
      apiClient.questionBank.copy(data),
    onSuccess: () => void invalidateQuestionBank(queryClient),
  });
}

export function useImportBankQuestionsToEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ evaluationId, questionIds }: { evaluationId: string; questionIds: string[] }) =>
      apiClient.evaluations.importBankQuestions(evaluationId, questionIds),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(variables.evaluationId) });
      void invalidateQuestionBank(queryClient);
    },
  });
}

export function toQuestionBankInput(question: QuestionBankQuestion): QuestionBankQuestionInput {
  return {
    programId: question.programId,
    subjectId: question.subjectId ?? undefined,
    question: question.question,
    type: question.type,
    explanation: question.explanation ?? undefined,
    points: question.points,
    options: question.options.map((option) => ({
      text: option.text,
      isCorrect: option.isCorrect,
      order: option.order,
    })),
  };
}
